import classNames from "classnames";
import React, { Component, createRef } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import Form, { Fields } from "Shared/Form";
import DateSelector from "Shared/Form/DateSelector";
import SSNInput from "Shared/Form/Input/SSNInput";
import { CanadianSSN, required, SSN } from "Shared/Form/validators";
import styled, { css } from "styled-components";
import { isUS } from "src/utils/account";
import { subtractYears } from "src/utils/date";
import { Account } from "Types/api/Account";
import { getRegion, isCanada } from "src/utils/location.ts";
import StepContent from "../../StepContent/StepContent";
import { BaseStepProps } from "../shared/BaseStepProps";
import { mapFieldsToValues } from "../shared/fieldValueMapper";
import Select from "Shared/Form/Select";
import { getStates } from "src/utils/states";
import { ASTERISK } from "src/constants";

const TRANSLATION_KEY = "ContinueDeal.CreditApp.Identification";

const ColumnStyle = css`
    display: inline-block;
    padding: 10px;
    vertical-align: top;
`;

const ColumnFull = styled("div")`
    ${ColumnStyle};
    width: calc(100% - 20px);
`;

const Title = styled("h3")`
    border-bottom: 2px solid #e5e5e5;
    color: #333;
    font-size: 1.5625em;
    font-weight: 400;
    padding: 0 0 15px;
    margin: 15px 0 0;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        font-size: 1.875em;
        padding: 0 0 8px;
    }
`;

const StyledForm = styled(Form)`
    margin: 20px 0;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        margin: 30px 0;
    }
`;
StyledForm.displayName = "IdentificationForm";

const ColumnMedium = styled("div")`
    ${ColumnStyle};
    width: calc(100% - 20px);

    ${(props) => props.theme.breakpoints.up("desktop")} {
        width: calc(50% - 20px);
    }
`;

export interface IdentificationProps extends BaseStepProps {
    account: Account;
}

class Identification extends Component<IdentificationProps & WithTranslation> {
    private readonly fieldsPerRow: string[][];
    private readonly fields: {};
    private identificationForm = createRef<Form>();

    constructor(props: IdentificationProps & WithTranslation) {
        super(props);

        const { formSettings = {} } = this.props;
        this.fieldsPerRow = this.getFieldsPerRow(formSettings);
        this.fields = this.getFields(formSettings);
    }

    public render() {
        const { className, isCosigner = false, formData = {}, account, t } = this.props;
        const fields = mapFieldsToValues(this.fields, formData);
        this.buildSSNField(fields, isUS(getRegion(account)));

        return (
            <div className={classNames("Identification", className)}>
                <StepContent onBackClick={this.handleBackClick} onNextClick={this.handleNextClick}>
                    <Title>
                        {isCosigner ? t(`${TRANSLATION_KEY}.cosignerTitle`) : t(`${TRANSLATION_KEY}.defaultTitle`)}
                    </Title>
                    <StyledForm fields={fields} fieldsPerRow={this.fieldsPerRow} ref={this.identificationForm} />
                </StepContent>
            </div>
        );
    }

    private getFields(formSettings: Record<string, string[]>) {
        const { t, account } = this.props;

        return {
            birthDay: {
                input: DateSelector,
                inputProps: {
                    rangeEnd: subtractYears(new Date(), 10),
                    rangeStart: new Date(1920, 1, 1),
                    type: "MDY",
                },
                label: `${t(`${TRANSLATION_KEY}.dateOfBirthLabel`)}*`,
                validators: [required],
                wrapper: ColumnFull,
            },
            ssn: {
                input: SSNInput,
                inputProps: {
                    isUS: true,
                },
                label: `${t(`${TRANSLATION_KEY}.socialSecurityNumberLabel`)}*`,
                validators: [],
                wrapper: ColumnMedium,
            },
            maritalStatus: {
                input: Select,
                inputProps: {
                    options: [
                        { label: t(`${TRANSLATION_KEY}.unmarriedLabel`), value: "Unmarried" },
                        { label: t(`${TRANSLATION_KEY}.marriedLabel`), value: "Married" },
                        { label: t(`${TRANSLATION_KEY}.separatedLabel`), value: "Separated" },
                    ],
                },
                label: `${t(`${TRANSLATION_KEY}.maritalStatusLabel`)}${ASTERISK}`,
                validators: [required],
                wrapper: ColumnMedium,
            },
            ...("primary_drivers_license" in formSettings && {
                driversLicense: {
                    label: `${t(`${TRANSLATION_KEY}.driversLicenseLabel`)}${ASTERISK}`,
                    validators: [required],
                    wrapper: ColumnMedium,
                },
                driversLicenseState: {
                    input: Select,
                    inputProps: { options: getStates(getRegion(account)) },
                    label: isCanada(account)
                        ? `${t(`${TRANSLATION_KEY}.driversLicenseProvince`)}${ASTERISK}`
                        : `${t(`${TRANSLATION_KEY}.driversLicenseState`)}${ASTERISK}`,
                    validators: [required],
                    wrapper: ColumnMedium,
                },
            }),
        };
    }

    private getFieldsPerRow(formSettings: Record<string, string[]>) {
        const fieldsPerRow = [["ssn", "maritalStatus"], ["birthDay"]];

        if ("primary_drivers_license" in formSettings) {
            fieldsPerRow.push(["driversLicense", "driversLicenseState"]);
        }
        return fieldsPerRow;
    }

    private handleNextClick = () => {
        if (this.identificationForm.current!.isFormValid()) {
            const formData = this.identificationForm.current!.getFormData();
            return this.props.onNextClick(formData);
        }
    };

    private handleBackClick = () => {
        const formData = this.identificationForm.current!.getFormData();
        return this.props.onBackClick(formData);
    };

    private buildSSNField = (fields: Fields, isUSInput: boolean) => {
        const { t } = this.props;

        fields.ssn.inputProps.isUS = isUSInput;
        fields.ssn.label = isUSInput
            ? `${t(`${TRANSLATION_KEY}.socialSecurityNumberLabel`)}*`
            : `${t(`${TRANSLATION_KEY}.socialInsuranceNumberLabel`)}`;
        fields.ssn.validators = isUSInput ? [required, SSN] : [CanadianSSN];
    };
}

export default withTranslation()(Identification);
